import { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { distance } from '@turf/distance'
import { point } from '@turf/helpers'

const LINE_SOURCE = 'relocation-line-source'
const LABEL_SOURCE = 'relocation-label-source'

/**
 * En modo edición, dibuja una línea entre la posición original del árbol
 * y la nueva posición capturada, mostrando la distancia en metros.
 *
 * Solo activo cuando enabled=true (addMode activo en editar-arbol).
 *
 * @param {{ map, layersReady, originCoords: [lng,lat]|null, enabled: boolean }} params
 */
export const useRelocationDistance = ({ map, layersReady, originCoords, enabled }) => {
  const coords = useSelector(state => state.mapState.coords)

  // Estabilizar referencia: solo cambia cuando los valores reales cambian
  const captureLngLat = useMemo(
    () => (coords?.lng != null ? [coords.lng, coords.lat] : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [coords?.lng, coords?.lat],
  )

  const isActive = enabled && originCoords != null && captureLngLat != null

  useEffect(() => {
    if (!map || !layersReady) return
    const isStyleReady = () => {
      try {
        return Boolean(map.getStyle?.()) && map.isStyleLoaded()
      } catch {
        return false
      }
    }
    if (!isStyleReady()) return

    // Sources
    try {
      if (!map.getSource(LINE_SOURCE)) {
        map.addSource(LINE_SOURCE, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        })
      }
      if (!map.getSource(LABEL_SOURCE)) {
        map.addSource(LABEL_SOURCE, {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] },
        })
      }
    } catch (_) {
      return
    }

    // Línea entre los dos puntos (empieza oculta)
    if (!map.getLayer('relocation-line')) {
      try {
        map.addLayer({
          id: 'relocation-line',
          type: 'line',
          slot: 'top',
          source: LINE_SOURCE,
          layout: { visibility: 'none' },
          paint: {
            'line-color': '#00e5ff',
            'line-width': 4.5,
            'line-dasharray': [4, 3],
            'line-opacity': 0.85,
            'line-emissive-strength': 2,
          },
        })
      } catch (_) {}
    }

    // Etiqueta de distancia en el punto medio (empieza oculta)
    if (!map.getLayer('relocation-label')) {
      try {
        map.addLayer({
          id: 'relocation-label',
          type: 'symbol',
          slot: 'top',
          source: LABEL_SOURCE,
          layout: {
            visibility: 'none',
            'text-field': ['get', 'label'],
            'text-size': 12,
            'text-offset': [0, -1.2],
            'text-anchor': 'bottom',
            'text-allow-overlap': true,
          },
          paint: {
            'text-color': '#ffffff',
            'text-halo-color': '#000000',
            'text-halo-width': 1.5,
            'text-emissive-strength': 1,
          },
        })
      } catch (_) {}
    }

    const lineSource = map.getSource(LINE_SOURCE)
    const labelSource = map.getSource(LABEL_SOURCE)

    if (isActive) {
      lineSource?.setData({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: { type: 'LineString', coordinates: [originCoords, captureLngLat] },
            properties: {},
          },
        ],
      })

      const dist = distance(point(originCoords), point(captureLngLat), {
        units: 'meters',
      })
      const label =
        dist < 1000 ? `${dist.toFixed(0)} m` : `${(dist / 1000).toFixed(2)} km`
      const midLng = (originCoords[0] + captureLngLat[0]) / 2
      const midLat = (originCoords[1] + captureLngLat[1]) / 2

      labelSource?.setData({
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [midLng, midLat] },
            properties: { label },
          },
        ],
      })

      try {
        map.setLayoutProperty('relocation-line', 'visibility', 'visible')
        map.setLayoutProperty('relocation-label', 'visibility', 'visible')
      } catch (_) {}
    } else {
      try {
        map.setLayoutProperty('relocation-line', 'visibility', 'none')
        map.setLayoutProperty('relocation-label', 'visibility', 'none')
      } catch (_) {}
      lineSource?.setData({ type: 'FeatureCollection', features: [] })
      labelSource?.setData({ type: 'FeatureCollection', features: [] })
    }
  }, [map, layersReady, enabled, isActive, originCoords, captureLngLat])
}

export default useRelocationDistance
